import { Coords } from './models/geolocation'

export const distanceBetweenCoordinates = (
  lat1: number | undefined,
  lon1: number | undefined,
  lat2: number,
  lon2: number,
  unit: 'M' | 'K' | 'N' = 'K'
) => {
  if (!lat1 || !lon1) return 0
  if (lat1 === lat2 && lon1 === lon2) {
    return 0
  } else {
    let dist = 0
    if (unit === 'K') {
      dist = getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2)
    }

    return dist
  }
}

// refactor using Vincenty Formula for ellipsoid (instead of Haversine formula for sphere)
export function getDistanceFromLatLonInMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  // WGS-84 ellipsoid parameters
  const a = 6378137.0 // equatorial radius in meters
  const b = 6356752.314245 // polar radius in meters
  const f = 1 / 298.257223563 // flattening

  // Convert to radians
  const phi1 = deg2rad(lat1)
  const lambda1 = deg2rad(lon1)
  const phi2 = deg2rad(lat2)
  const lambda2 = deg2rad(lon2)

  const L = lambda2 - lambda1 // difference in longitude

  // Reduced latitudes
  const U1 = Math.atan((1 - f) * Math.tan(phi1))
  const U2 = Math.atan((1 - f) * Math.tan(phi2))

  const sinU1 = Math.sin(U1)
  const cosU1 = Math.cos(U1)
  const sinU2 = Math.sin(U2)
  const cosU2 = Math.cos(U2)

  let lambda = L
  let lambdaP = 2 * Math.PI
  let iterLimit = 20
  let cosSqAlpha = 0
  let sinSigma = 0
  let cos2SigmaM = 0
  let cosSigma = 0
  let sigma = 0

  while (Math.abs(lambda - lambdaP) > 1e-12 && --iterLimit > 0) {
    const sinLambda = Math.sin(lambda)
    const cosLambda = Math.cos(lambda)

    sinSigma = Math.sqrt(
      (cosU2 * sinLambda) ** 2 + (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) ** 2
    )

    if (sinSigma === 0) return 0 // coincident points

    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda
    sigma = Math.atan2(sinSigma, cosSigma)
    const sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma
    cosSqAlpha = 1 - sinAlpha ** 2

    cos2SigmaM = cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha
    if (isNaN(cos2SigmaM)) cos2SigmaM = 0 // equatorial line

    const C = (f / 16) * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha))
    lambdaP = lambda
    lambda =
      L +
      (1 - C) *
        f *
        sinAlpha *
        (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)))
  }

  if (iterLimit === 0) return 0 // Changed from NaN to 0 for better handling

  // Safety checks to prevent undefined values
  if (typeof cosSqAlpha === 'undefined' || isNaN(cosSqAlpha)) cosSqAlpha = 0
  if (typeof sinSigma === 'undefined' || isNaN(sinSigma)) sinSigma = 0
  if (typeof cos2SigmaM === 'undefined' || isNaN(cos2SigmaM)) cos2SigmaM = 0
  if (typeof cosSigma === 'undefined' || isNaN(cosSigma)) cosSigma = 0
  if (typeof sigma === 'undefined' || isNaN(sigma)) sigma = 0

  const uSq = (cosSqAlpha * (a ** 2 - b ** 2)) / b ** 2
  const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)))
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)))

  const deltaSigma =
    B *
    sinSigma *
    (cos2SigmaM +
      (B / 4) *
        (cosSigma * (-1 + 2 * cos2SigmaM ** 2) -
          (B / 6) * cos2SigmaM * (-3 + 4 * sinSigma ** 2) * (-3 + 4 * cos2SigmaM ** 2)))

  const distance = b * A * (sigma - deltaSigma)

  return isNaN(distance) ? 0 : distance // Final safety check
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180)
}

export const separateNumberFromDirection = ({ mainText = '' }) => {
  const splitedDirection = mainText.split(' ')
  const last = splitedDirection[splitedDirection.length - 1]
  const isNumber = !isNaN(Number(last))
  const number = isNumber ? last : ''
  return { number, direction: mainText }
}

export const compareSameCoords = ({ coord1, coord2 }: { coord1: Coords; coord2: Coords }) => {
  return coord1.lat === coord2.lat && coord1.lng === coord2.lng
}
